import React, { useEffect, useState } from "react";
import { db, auth } from "../firebaseConfig";
import { collection, getDocs, deleteDoc, doc, getDoc } from "firebase/firestore";
import { Button, Card, Col, Row, ProgressBar, Form, Alert, Table } from "react-bootstrap";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import CryptoJS from "crypto-js";

const CLOUDINARY_API_SECRET = process.env.REACT_APP_CLOUDINARY_API_SECRET;
const CLOUDINARY_API_KEY = process.env.REACT_APP_CLOUDINARY_API_KEY;
const CLOUDINARY_CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;


console.log(CLOUDINARY_API_KEY);
console.log(CLOUDINARY_API_SECRET)
console.log(CLOUDINARY_CLOUD_NAME)

export default function StorageMaintenance() {
  const [files, setFiles] = useState([]);
  const [signedDocuments, setSignedDocuments] = useState([]);
  const [totalStorageUsed, setTotalStorageUsed] = useState(0);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const MAX_STORAGE = 25 * 1024 * 1024 * 1024; // 25GB in bytes

  // Check if the user is an admin
  const checkAdminRole = async () => {
    try {
      const user = auth.currentUser;
      if (user) {
        const userDoc = await getDoc(doc(db, "Users", user.uid));
        if (userDoc.exists() && userDoc.data().role === "admin") {
          setIsAdmin(true);
        } else {
          setIsAdmin(false);
        }
      }
    } catch (err) {
      console.error("Error checking user role:", err);
      setIsAdmin(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAdminRole();
  }, []);

  // Fetch files from Firestore
  const fetchFiles = async () => {
    try {
      const fileSnapshot = await getDocs(collection(db, "Files"));
      const fileList = fileSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setFiles(fileList);

      // Calculate total storage used
      const totalSize = fileList.reduce((acc, file) => acc + file.size, 0);
      setTotalStorageUsed(totalSize);
    } catch (err) {
      console.error("Error fetching files:", err);
    }
  };

  // Fetch signed documents from Firestore
  const fetchSignedDocuments = async () => {
    try {
      const signedDocsSnapshot = await getDocs(collection(db, "SignedDocuments"));
      const signedDocsList = signedDocsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setSignedDocuments(signedDocsList);
    } catch (err) {
      console.error("Error fetching signed documents:", err);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchFiles();
      fetchSignedDocuments();
    }
  }, [isAdmin]);

  // Delete a file from Cloudinary
  const deleteFromCloudinary = async (fileUrl) => {
    try {
      const resourceType = fileUrl.includes(".pdf") ? "image" : "raw";
      const publicId = fileUrl.split("/").pop().split(".")[0];

      // Generate timestamp and signature
      const timestamp = Math.floor(Date.now() / 1000);
      const stringToSign = `public_id=${publicId}&timestamp=${timestamp}${CLOUDINARY_API_SECRET}`;
      const signature = CryptoJS.SHA1(stringToSign).toString(CryptoJS.enc.Hex);

      // Make the delete request
      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/${resourceType}/destroy`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            public_id: publicId,
            timestamp,
            signature,
            api_key: CLOUDINARY_API_KEY,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete file from Cloudinary");
      }

      console.log(response)

      console.log(`File ${publicId} deleted from Cloudinary.`);
    } catch (err) {
      console.error("Error deleting from Cloudinary:", err);
      throw err;
    }
  };

  // Delete files by date range
  const handleDeleteByDateRange = async () => {
    if (!startDate || !endDate) {
      alert("Please select both start and end dates.");
      return;
    }

    const start = new Date(startDate).getTime();
    const end = new Date(endDate).getTime();

    const filesToDelete = files.filter((file) => {
      const uploadedAt = new Date(file.uploadedAt.seconds * 1000).getTime();
      return uploadedAt >= start && uploadedAt <= end;
    });

    if (filesToDelete.length === 0) {
      alert("No files found in the specified date range.");
      return;
    }

    try {
      for (const file of filesToDelete) {
        await deleteFromCloudinary(file.url);
        await deleteDoc(doc(db, "Files", file.id));
      }

      alert("Files deleted successfully!");
      fetchFiles();
    } catch (err) {
      console.error("Error deleting files:", err);
      alert("Failed to delete files.");
    }
  };

  // Delete files older than 20 days
  const handleDeleteOldFiles = async () => {
    const now = new Date().getTime();
    const days20 = 20 * 24 * 60 * 60 * 1000;

    const filesToDelete = files.filter((file) => {
      const uploadedAt = new Date(file.uploadedAt.seconds * 1000).getTime();
      return now - uploadedAt > days20;
    });

    if (filesToDelete.length === 0) {
      alert("No files older than 20 days were found.");
      return;
    }

    try {
      for (const file of filesToDelete) {
        await deleteFromCloudinary(file.url);
        await deleteDoc(doc(db, "Files", file.id));
      }

      alert("Files older than 20 days have been deleted successfully!");
      fetchFiles();
    } catch (err) {
      console.error("Error deleting old files:", err);
      alert("Failed to delete old files.");
    }
  };

  // Delete a signed document
  const handleDeleteSignedDocument = async (docId) => {
    try {
      const docRef = signedDocuments.find((doc) => doc.id === docId);

      if (docRef) {
        if (docRef.signedUrl) await deleteFromCloudinary(docRef.signedUrl);
        if (docRef.unsignedUrl) await deleteFromCloudinary(docRef.unsignedUrl);
      }

      await deleteDoc(doc(db, "SignedDocuments", docId));
      alert("Signed document deleted successfully!");
      fetchSignedDocuments();
    } catch (err) {
      console.error("Error deleting signed document:", err);
      alert("Failed to delete signed document.");
    }
  };

  if (loading) {
    return (
      <div className="text-center mt-5">
        <h4>Loading...</h4>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="text-center mt-5">
        <Alert variant="danger">You do not have permission to access this page.</Alert>
      </div>
    );
  }

  return (
    <React.Fragment>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <h4 className="main-title mb-4">Storage Maintenance</h4>
        <Row className="mb-4">
          <Col md="6">
            <Card className="shadow-sm">
              <Card.Body>
                <h5>Storage Usage</h5>
                <p>
                  Total Storage Used:{" "}
                  <strong>{(totalStorageUsed / (1024 * 1024 * 1024)).toFixed(2)} GB</strong>
                  / 25 GB
                </p>
                <ProgressBar
                  now={(totalStorageUsed / MAX_STORAGE) * 100}
                  label={`${((totalStorageUsed / MAX_STORAGE) * 100).toFixed(2)}%`}
                />
              </Card.Body>
            </Card>
          </Col>
          <Col md="6">
            <Card className="shadow-sm">
              <Card.Body>
                <h5>Delete Files by Date Range</h5>
                <Form.Group className="mb-3">
                  <Form.Label>Start Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>End Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </Form.Group>
                <Button variant="danger" onClick={handleDeleteByDateRange}>
                  Delete Files
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Card className="shadow-sm mb-4">
          <Card.Body>
            <h5>Delete Files Older than 20 Days</h5>
            <p>Use the button below to delete all files that were uploaded more than 20 days ago.</p>
            <Button variant="warning" onClick={handleDeleteOldFiles}>
              Delete Files Older than 20 Days
            </Button>
          </Card.Body>
        </Card>

        <Card className="shadow-sm">
          <Card.Body>
            <h5>Signed Documents</h5>
            <Alert variant="info">Below is a list of all signed documents.</Alert>
            <Table striped>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Signed Status</th>
                  <th>Signed URL</th>
                  <th>Unsigned URL</th>
                  <th>Created At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {signedDocuments.map((doc) => (
                  <tr key={doc.id}>
                    <td>{doc.createdFor}</td>
                    <td>{doc.signedStatus}</td>
                    <td>
                      <a href={doc.signedUrl} target="_blank" rel="noopener noreferrer">
                        View Signed Document
                      </a>
                    </td>
                    <td>
                      <a href={doc.unsignedUrl} target="_blank" rel="noopener noreferrer">
                        View Unsigned Document
                      </a>
                    </td>
                    <td>{new Date(doc.createdAt.seconds * 1000).toLocaleDateString()}</td>
                    <td>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleDeleteSignedDocument(doc.id)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      </div>
      <Footer />
    </React.Fragment>
  );
}
